import type { ButtonHTMLAttributes, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { useChatComposer, useChatIsStreaming, useChatSubmit, useChatStop, useChatIsInputDisabled, useChatOnDisabledClick, useChatConcurrentSubmit } from "../ChatContext";

export interface SubmitButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "type" | "onClick"
> {
  onStop?: () => void;
  children?: ReactNode;
  stopIcon?: ReactNode;
  /**
   * External enabled state (e.g., false when out of credits).
   * Stop mode is still allowed regardless of this prop.
   * @default true
   */
  isEnabled?: boolean;
}

export function SubmitButton({
  onStop,
  disabled,
  className,
  children,
  stopIcon,
  isEnabled: externalEnabled = true,
  ...props
}: SubmitButtonProps) {
  const composer = useChatComposer();
  const isStreaming = useChatIsStreaming();
  const submit = useChatSubmit();
  const stop = useChatStop();
  const chatDisabled = useChatIsInputDisabled();
  const onDisabledClick = useChatOnDisabledClick();
  const concurrentSubmit = useChatConcurrentSubmit();

  // Stop mode is opt-in via stopIcon. When concurrent submit is enabled
  // we don't enter stop mode on streaming — submitting routes to the
  // agent inbox instead, and the user can use a separate cancel/stop
  // affordance (or the built-in stop()) if they want to interrupt.
  const hasStopMode = stopIcon !== undefined;
  const isInStopMode = hasStopMode && isStreaming && !concurrentSubmit;

  // Concurrent submit: keep the button live during a stream so the user
  // can type a follow-up that lands on the agent's inbox at the next
  // beforeModel boundary. Default behavior (concurrentSubmit=false)
  // gates submit on !isStreaming as before.
  const canSubmit = composer.isReady && (concurrentSubmit || !isStreaming);
  const isEnabled = (canSubmit && externalEnabled && !chatDisabled) || isInStopMode;

  const handleClick = () => {
    if (isInStopMode) {
      if (onStop) {
        onStop();
      } else {
        stop();
      }
    } else if (chatDisabled && onDisabledClick) {
      onDisabledClick();
    } else if (canSubmit && externalEnabled && !chatDisabled) {
      submit();
    }
  };

  // When chatDisabled with an onDisabledClick handler, keep the button
  // clickable (not disabled) so the handler fires.
  const shouldDisable = chatDisabled && onDisabledClick
    ? false
    : disabled ?? !isEnabled;

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={shouldDisable}
      className={twMerge(
        "flex items-center justify-center",
        "transition-colors",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        className
      )}
      data-testid="send-button"
      data-disabled-reason={!externalEnabled ? "external" : undefined}
      aria-label={isInStopMode ? "Stop" : "Send message"}
      {...props}
    >
      {isInStopMode ? stopIcon : children}
    </button>
  );
}
